import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { X } from "@/components/icons";

import { cn } from "@/lib/utils";
import { SheetGrabber } from "@/components/ui/sheet";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

function DialogOverlay({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-black/35 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

const PHONE_SHEET_CLASS =
  "max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:mx-auto max-md:w-full max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-b-none max-md:rounded-t-2xl max-md:border-x-0 max-md:border-b-0 max-md:max-h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:zoom-in-100 max-md:data-[state=closed]:zoom-out-100";

interface DialogContentProps extends React.ComponentProps<
  typeof DialogPrimitive.Content
> {
  hideCloseButton?: boolean;
  overlayClassName?: string;
  layout?: "default" | "sectioned" | "window";
  phoneLayout?: "sheet" | "custom";
}

function DialogContent({
  ref,
  className,
  children,
  hideCloseButton,
  overlayClassName,
  layout = "default",
  phoneLayout = "sheet",
  ...props
}: DialogContentProps) {
  const isPhoneSheet = phoneLayout === "sheet" && layout !== "window";
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={props["aria-describedby"] ?? undefined}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-xl border border-border bg-surface p-4 shadow-modal duration-slow data-[state=open]:ease-out data-[state=closed]:ease-in sm:p-6 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 max-h-[calc(100dvh-2rem)] overflow-y-auto",
          layout !== "default" &&
            "flex flex-col gap-0 overflow-hidden p-0 sm:p-0",
          layout === "window" &&
            "h-[min(52rem,calc(100dvh-2rem))] max-w-7xl max-md:h-dvh max-md:max-h-dvh max-md:w-full max-md:max-w-none max-md:rounded-none max-md:border-0",
          isPhoneSheet && PHONE_SHEET_CLASS,
          isPhoneSheet &&
            (layout === "sectioned"
              ? "max-md:pb-[env(safe-area-inset-bottom)]"
              : "max-md:pb-[max(1rem,env(safe-area-inset-bottom))]"),
          className,
        )}
        {...props}
      >
        {isPhoneSheet ? <SheetGrabber className="md:hidden" /> : null}
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-md text-fg-subtle pointer-coarse:after:absolute pointer-coarse:after:-inset-3.5 ring-offset-background transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-[3px] focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "sticky bottom-0 z-10 flex flex-col-reverse gap-2 bg-surface shadow-[0_1rem_0_var(--surface)] sm:flex-row sm:justify-end sm:gap-3 sm:shadow-[0_1.5rem_0_var(--surface)]",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

function DialogTitle({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-h3 leading-none tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-body text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
